import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftRight, Coffee, Flower2, GraduationCap, Heart, Lightbulb, PenLine, Shapes } from 'lucide-react';
import { CATEGORIES } from '../../data/vocabulary';
import Reveal from '../Reveal';

const ICONS = { ArrowLeftRight, Coffee, Flower2, GraduationCap, Heart, Lightbulb, PenLine, Shapes };

// Bento placement: one large feature tile, one wide tile, the rest single
const SPANS = {
  beautiful: 'sm:col-span-2 lg:col-span-2 lg:row-span-2',
  writers: 'sm:col-span-2 lg:col-span-2',
};

const countWords = (category) => (category.pairs ? category.pairs.length * 2 : category.words.length);

const previewOf = (category) =>
  category.pairs
    ? category.pairs.slice(0, 2).map((pair) => pair.join(' or ')).join(', ')
    : category.words.slice(0, 3).join(', ');

const WordLink = ({ word }) => (
  <Link
    to={`/define/${word}`}
    className="inline-block rounded-full border border-ink/10 bg-surface px-4 py-2 text-lg text-ink transition-[border-color,background-color,transform] hover:border-ink/40 hover:bg-butter active:scale-95"
  >
    {word}
  </Link>
);

const CategoryExplorer = () => {
  const [activeId, setActiveId] = useState(CATEGORIES[0].id);
  const active = CATEGORIES.find((c) => c.id === activeId);

  return (
    <section aria-labelledby="explore-heading" className="wrap pb-16 md:pb-24">
      <Reveal className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 id="explore-heading" className="font-display text-4xl leading-tight text-ink md:text-5xl">
            Explore words by mood
          </h2>
          <p className="mt-3 max-w-xl text-lg text-ink-soft">
            Pick a collection to see its words, then open any word for the full entry.
          </p>
        </div>
      </Reveal>

      <Reveal delay={80} className="mt-10 grid auto-rows-[minmax(10.5rem,auto)] gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {CATEGORIES.map((category) => {
          const Icon = ICONS[category.icon];
          const isActive = category.id === activeId;
          const isFeature = category.id === 'beautiful';
          return (
            <button
              key={category.id}
              type="button"
              onClick={() => setActiveId(category.id)}
              aria-pressed={isActive}
              aria-controls="category-words"
              className={`group relative flex flex-col overflow-hidden rounded-[1.75rem] border-2 p-6 text-left transition-[border-color,transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_-28px_rgb(var(--shade)/0.6)] active:scale-[0.99] ${
                category.color
              } ${isActive ? 'border-ink' : 'border-ink/[0.06]'} ${SPANS[category.id] || ''}`}
            >
              <span className="grid h-11 w-11 place-items-center rounded-full bg-surface/80 text-ink">
                <Icon size={20} aria-hidden="true" />
              </span>
              <span className={`mt-5 block font-display text-ink ${isFeature ? 'text-3xl md:text-4xl' : 'text-2xl'}`}>
                {category.name}
              </span>
              <span className="mt-2 block text-[0.95rem] leading-snug text-ink-soft">{category.description}</span>

              {isFeature && (
                <span aria-hidden="true" className="mt-auto block pt-8 font-display text-[clamp(2.5rem,5vw,4rem)] leading-none text-ink/[0.14]">
                  mellifluous
                </span>
              )}

              <span className={`block pt-5 text-sm text-ink-soft ${isFeature ? '' : 'mt-auto'}`}>
                {previewOf(category)}… <span className="whitespace-nowrap">{countWords(category)} words</span>
              </span>
            </button>
          );
        })}
      </Reveal>

      <div
        id="category-words"
        aria-live="polite"
        key={active.id}
        className={`feedback-in mt-4 rounded-[1.75rem] border-2 border-ink p-6 sm:p-8 ${active.color}`}
      >
        <h3 className="font-display text-2xl text-ink">{active.name}</h3>
        {active.pairs ? (
          <ul className="mt-5 grid gap-3 sm:grid-cols-2">
            {active.pairs.map(([first, second]) => (
              <li key={first} className="flex flex-wrap items-center gap-3">
                <WordLink word={first} />
                <span className="text-ink-soft">or</span>
                <WordLink word={second} />
              </li>
            ))}
          </ul>
        ) : (
          <ul className="mt-5 flex flex-wrap gap-2.5">
            {active.words.map((word) => (
              <li key={word}>
                <WordLink word={word} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

export default CategoryExplorer;
